import { Modal } from '@design/components/modal/Modal';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useCustomSources } from '@terminal/react-query-hooks/alfred/useCustomSources';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { updateCustomSources } from '@terminal/views/alfred/research/services/conversations-service';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatWebsiteInfo, WebsiteInfo } from '../utils/formatWebsiteInfo';
import { InlineAlert } from './InlineAlert';
import { LinkInput } from './LinkInput';
import { DroppableArea, MAX_CUSTOM_SOURCES } from './SourcesDraggableArea';
import { SourcesUpdateAlert } from './SourcesUpdateAlert';
import styles from './TrustedSourcesModal.module.scss';

interface TrustedSourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TrustedSourcesModal = ({
  isOpen,
  onClose,
}: TrustedSourcesModalProps) => {
  const { t } = useTranslation(['chat', 'common']);

  const [customSources, setCustomSources] = useState<WebsiteInfo[]>([]);
  const [isOnlySource, setIsOnlySource] = useState(false);

  const { trackAlfredTrustedSourceAdded, trackAlfredTrustedSourceRemoved } =
    usePosthogEvents();
  const showToast = useToasts(state => state.showToast);

  const { data: customSourcesData, refetch: refetchCustomSources } =
    useCustomSources(isOpen);

  useEffect(() => {
    if (customSourcesData?.data?.sources) {
      setCustomSources(
        formatWebsiteInfo(customSourcesData?.data?.sources ?? [])
      );
    }
  }, [customSourcesData]);

  const handleAddWebsites = (websites: WebsiteInfo[]) => {
    const existedSources = customSources.map(source => source.url);
    const uniqueWebsites = websites.filter(
      website => !existedSources.includes(website.url)
    );

    if (uniqueWebsites.length > 0) {
      trackAlfredTrustedSourceAdded(POSTHOG_TRACKING_SOURCES.ALFRED);
    }

    const newSources = [...customSources, ...uniqueWebsites];
    setCustomSources(newSources);

    if (newSources.length > 1) {
      setIsOnlySource(false);
    }
  };

  const handleUpdateCustomSources = async () => {
    const response = await updateCustomSources({
      sources: customSources.map(source => source.url),
    });

    if (response) {
      onClose();
    }

    if (response.data) {
      refetchCustomSources();
      showToast({
        id: 'update-sources-success',
        position: ToastPosition.TopRight,
        content: <SourcesUpdateAlert />,
      });
    }
  };

  const handleRemoveSource = (id: string) => {
    if (customSources.length === 1) {
      setIsOnlySource(true);
    } else {
      trackAlfredTrustedSourceRemoved(POSTHOG_TRACKING_SOURCES.ALFRED);
      setCustomSources(customSources.filter(source => source.id !== id));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over) {
      setCustomSources(currentSources => {
        const activeIndex = currentSources.findIndex(
          source => source.id === active.id
        );
        const overIndex = currentSources.findIndex(
          source => source.id === over.id
        );

        if (activeIndex === -1 || overIndex === -1) {
          return currentSources;
        }

        return arrayMove(currentSources, activeIndex, overIndex);
      });
    }
  };

  const getModalProps = () => {
    return {
      title: t('chat:trustedSources.modal.title'),
      description: (
        <p>
          {t('chat:trustedSources.modal.description_1')}
          <br />
          <br />
          {t('chat:trustedSources.modal.description_2')}
        </p>
      ),
      primaryBtnProps: {
        label: t('chat:trustedSources.modal.saveTrustedSources'),
        disabled: customSources.length === 0,
        onClick: handleUpdateCustomSources,
      },
      cancelBtnProps: {
        label: t('common:cancel'),
        onClick: onClose,
      },
    };
  };

  return (
    <Modal
      className={styles.styledModal}
      isOpen={isOpen}
      onClose={() => {
        setCustomSources([]);
        onClose();
      }}
      {...getModalProps()}
      disableEnterClose
      outsideClickClose={false}
      data-testid="trusted-sources-modal"
    >
      <div className={styles.content}>
        <div className={styles.sources}>
          <DndContext onDragEnd={handleDragEnd}>
            <SortableContext
              items={customSources.map(source => source.id)}
              strategy={verticalListSortingStrategy}
            >
              <DroppableArea
                items={customSources}
                handleRemove={handleRemoveSource}
              />
            </SortableContext>
          </DndContext>
          {isOnlySource && <InlineAlert alertType="onlyOneSource" />}
        </div>
        {customSources.length >= MAX_CUSTOM_SOURCES ? (
          <InlineAlert alertType="exceededLimit" />
        ) : (
          <LinkInput
            sourceCount={customSources.length}
            handleAddWebsites={handleAddWebsites}
          />
        )}
      </div>
    </Modal>
  );
};
